import { Edit as EditIcon, Save as SaveIcon } from '@mui/icons-material';
import { Box, Button, Card, CircularProgress, SxProps, Theme, Tooltip, Typography } from '@mui/material';
import { AddProductDialog } from 'components/Quote/AddProductDialog';
import { CustomerManager } from 'components/Quote/CustomerManager';
import { QuoteLineTable } from 'components/Quote/QuoteLineTable';
import { QuoteStats } from 'components/Quote/QuoteStats';
import { useDocs } from 'hooks/useDocs';
import { AsyncTaskState } from 'library/async';
import { toast } from 'material-react-toastify';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useTitle } from 'react-use';
import { Navigator } from 'shared/components/Navigator';
import { routes, saveStateStrings } from 'shared/constants';
import { getTotalQuoteCost, getTotalQuoteRevenue } from 'shared/helpers';
import { Customer } from 'shared/types/customer';
import { defaultQuote, Quote as QuoteType } from 'shared/types/quote';
import { QuoteLine } from 'shared/types/quoteline';
import { NavigatorLayout } from './NavigatorLayout';

const quoteLineContainerStyles: SxProps<Theme> = {
  width: '100%',
  minHeight: 350,
  flex: '1 1 auto',
  margin: '15px 0px 15px 0px',
  padding: '15px',
  display: 'flex',
  flexDirection: 'column',
};
const quoteLineContainerHeaderStyles: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'space-between',
  marginBottom: '30px',
  flexWrap: 'wrap',
};
const quoteLineContainerBodyStyles: SxProps<Theme> = { overflow: 'auto', flexGrow: 1 };
const centeredContentStyles: SxProps<Theme> = {
  height: '100%',
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
};
const columnStyles: SxProps<Theme> = { display: 'flex', flexDirection: 'column', alignItems: 'center' };

interface QuoteProps {
  displayName: string;
}
export const Quote = ({ displayName }: QuoteProps) => {
  const [formState, setFormState] = useState<QuoteType>(defaultQuote);
  const [loading, setLoading] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(true);
  const [showAddProducts, setShowAddProducts] = useState(false);
  const history = useHistory();
  // const { clientApp: userManager } = useAppContext();
  // const { name: createdBy } = userManager.getAccount();
  const createdBy = displayName;

  const { saveDoc, saveDocTask, getDoc, updateDoc, updateDocTask } = useDocs<QuoteType>({
    clientId: 'demo-plq',
    documentType: 'quote',
  });

  // If Existing, get the Quote
  const { id } = useParams<{ id: string }>();
  const totalRevenue = useMemo(() => getTotalQuoteRevenue(formState), [formState]);

  useEffect(() => {
    setFormState(state => ({ ...state, revenue: totalRevenue ?? 0 }));
  }, [totalRevenue]);
  useEffect(() => {
    if (!id) return;

    setLoading(true);
    getDoc(id)
      .then(res => {
        if (res) {
          const {
            id: resId,
            customer,
            quoteLines,
            createdBy,
            createDate,
            expirationDate,
            customer_name,
            customer_number,
            revenue,
          } = res;

          setFormState({
            id: resId,
            customer_name,
            customer_number,
            revenue,
            customer,
            quoteLines,
            createdBy,
            createDate,
            expirationDate,
          });
        }
        setEditingCustomer(false);
      })
      .catch(error => {
        toast.error('This quote was not found.');
        setTimeout(() => {
          history.push(routes.landingPage);
        }, 3000);
      })
      .finally(() => setLoading(false));
  }, [id, getDoc, setFormState, setLoading, history]);

  const isExisting = useMemo(() => !!formState.id, [formState.id]);

  const handleSetCustomer = useCallback(
    (customer: Customer) => {
      const { customer_name, customer_number } = customer;
      setFormState(state => ({ ...state, customer_name, customer_number, quoteLines: [], customer }));
      setEditingCustomer(false);
    },
    [setFormState]
  );

  const handleSetQuoteLines = useCallback(
    (quoteLines: QuoteLine[]) => {
      setFormState(state => ({
        ...state,
        quoteLines,
      }));
    },
    [setFormState]
  );

  const handleReviseDiscount = useCallback(
    (lineNumber: number, newDiscount: number) => {
      const tempArr = formState.quoteLines;
      tempArr[lineNumber] = { ...tempArr[lineNumber], revisedDiscount: newDiscount };
      setFormState(state => ({
        ...state,
        quoteLines: [...tempArr],
      }));
    },
    [formState, setFormState]
  );

  const handleSetExpanded = useCallback(
    (index: number) => {
      setFormState(state => ({
        ...state,
        quoteLines: state.quoteLines.map((row, i) => ({ ...row, isExpanded: index === i ? !row.isExpanded : false })),
      }));
    },
    [setFormState]
  );

  const handleSave = useCallback(() => {
    if (!formState.customer || formState.quoteLines.length === 0) return;

    if (id) {
      updateDoc(id, { ...formState })
        .then(res => {
          toast.success('Changes saved!');
        })
        .catch(e => toast.error('Something went wrong'));
      return;
    }
    const createDate = new Date().toISOString();
    const quote = { ...formState, createdBy, createDate };
    saveDoc(quote)
      .then(res => {
        if (!res) {
          // is it possible to get a 200/201 and not get a response body?
          toast.warning('Your quote may not have been saved.');
          return;
        }
        toast.success('Your quote has been created!');
        history.push(routes.viewQuote.replace(':id', res.id));
      })
      .catch(error => toast.error('Your quote could not be saved.'));
  }, [formState, saveDoc, createdBy, history, id, updateDoc]);

  const handleStartNewQuote = useCallback(() => {
    history.push(routes.createQuote);
    setEditingCustomer(true);
    setFormState(defaultQuote);
  }, [history, setEditingCustomer, setFormState]);

  const isSaving = useMemo(
    () =>
      saveDocTask.taskState === AsyncTaskState.IN_PROGRESS || updateDocTask.taskState === AsyncTaskState.IN_PROGRESS,
    [saveDocTask, updateDocTask]
  );

  const margin = useMemo(() => {
    const totalCost = getTotalQuoteCost(formState);
    if (!totalRevenue || !totalCost) return undefined;
    return (totalRevenue - totalCost) / totalRevenue;
  }, [totalRevenue, formState]);

  const title = useMemo(
    () => (formState.id ? `${formState.customer.customer_number} Quote ${formState.id.slice(0, 4)}` : 'New Quote'),
    [formState]
  );
  useTitle(title);

  if (loading) {
    return (
      <Box sx={centeredContentStyles}>
        <Box sx={columnStyles}>
          <Typography variant='h5'>Retrieving Quote</Typography>
          <CircularProgress color='secondary' />
        </Box>
      </Box>
    );
  }

  return (
    <>
      <NavigatorLayout>
        <Navigator links={[{ to: routes.listQuotes, text: 'Quotes' }]} last={isExisting ? id : 'New'} />
        <Button variant='contained' color='primary' onClick={handleStartNewQuote}>
          New Quote
        </Button>
      </NavigatorLayout>
      <CustomerManager
        editingCustomer={editingCustomer}
        setEditingCustomer={setEditingCustomer}
        handleSetCustomer={handleSetCustomer}
        customer={formState.customer}
      />
      <QuoteStats
        revenue={totalRevenue}
        margin={margin}
        createdBy={formState.createdBy}
        createDate={formState.createDate}
      />
      <Card sx={quoteLineContainerStyles}>
        <Box sx={quoteLineContainerHeaderStyles}>
          <Typography variant='h5'>Products</Typography>
          {formState.quoteLines.length > 0 && (
            <div>
              <Button
                variant='contained'
                color='primary'
                disabled={isSaving}
                startIcon={isSaving ? <CircularProgress color='secondary' size={20} /> : <SaveIcon />}
                onClick={handleSave}
                style={{ marginRight: '15px' }}
              >
                {saveStateStrings[saveDocTask.taskState]}
              </Button>
              <Button
                startIcon={<EditIcon />}
                color='secondary'
                variant='outlined'
                onClick={() => setShowAddProducts(true)}
              >
                Edit Products
              </Button>
            </div>
          )}
        </Box>
        <Box sx={quoteLineContainerBodyStyles}>
          {formState.quoteLines.length > 0 ? (
            <QuoteLineTable
              lines={formState.quoteLines}
              onReviseDiscount={handleReviseDiscount}
              setIsExpanded={handleSetExpanded}
            />
          ) : (
            <Box sx={centeredContentStyles}>
              <Tooltip title='Please select a customer first' placement='top'>
                <span>
                  <Button
                    disabled={!formState.customer.uniqueid}
                    variant='contained'
                    color='primary'
                    onClick={() => {
                      setShowAddProducts(true);
                    }}
                  >
                    Add Products
                  </Button>
                </span>
              </Tooltip>
            </Box>
          )}
        </Box>
        <AddProductDialog
          open={showAddProducts}
          onClose={(event: any, reason: string) =>
            !['backdropClick', 'escapeKeyDown'].includes(reason) && setShowAddProducts(false)
          }
          customer={formState.customer}
          quoteLines={formState.quoteLines}
          handleSetQuoteLines={handleSetQuoteLines}
        />
      </Card>
    </>
  );
};
